import { computed, type Ref } from 'vue'
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/vue-query'
import { api } from './client'
import type { components } from './types'

export type Vehicle = components['schemas']['VehicleResponse']
export type VehicleListResponse = components['schemas']['VehicleListResponse']
export type VehicleCreate = components['schemas']['VehicleCreate']
export type VehicleUpdate = components['schemas']['VehicleUpdate']
export type VehicleTelemetryResponse = components['schemas']['VehicleTelemetryResponse']
export type VehicleActivityResponse = components['schemas']['VehicleActivityResponse']
export type VehicleRecentPlatesResponse = components['schemas']['RecentPlatesResponse']

export interface DateWindow {
  since: Date
  until: Date
}

export function useVehiclesList() {
  return useQuery({
    queryKey: ['vehicles', 'list'],
    queryFn: async () => {
      const response = await api.get<VehicleListResponse>('/vehicles')
      return response.data
    },
    refetchInterval: 30_000,
    placeholderData: keepPreviousData,
  })
}

export function useVehicleDetail(id: Ref<string>) {
  return useQuery({
    queryKey: ['vehicles', 'detail', id],
    queryFn: async () => {
      const response = await api.get<Vehicle>(`/vehicles/${id.value}`)
      return response.data
    },
    enabled: computed(() => !!id.value),
    staleTime: 30_000,
    // Single-row poll so header (online pill, last_seen) остаётся живым.
    refetchInterval: 30_000,
  })
}

export function useVehicleTelemetry(id: Ref<string>, window: Ref<DateWindow>) {
  return useQuery({
    queryKey: ['vehicles', 'telemetry', id, window],
    queryFn: async () => {
      const response = await api.get<VehicleTelemetryResponse>(
        `/vehicles/${id.value}/telemetry`,
        {
          params: {
            since: window.value.since.toISOString(),
            until: window.value.until.toISOString(),
          },
        },
      )
      return response.data
    },
    enabled: computed(() => !!id.value),
    staleTime: 30_000,
  })
}

export function useVehicleActivity(id: Ref<string>, window: Ref<DateWindow>) {
  return useQuery({
    queryKey: ['vehicles', 'activity', id, window],
    queryFn: async () => {
      const response = await api.get<VehicleActivityResponse>(
        `/vehicles/${id.value}/activity`,
        {
          params: {
            since: window.value.since.toISOString(),
            until: window.value.until.toISOString(),
          },
        },
      )
      return response.data
    },
    enabled: computed(() => !!id.value),
    staleTime: 30_000,
  })
}

export function useVehicleRecentPlates(
  id: Ref<string>,
  window: Ref<DateWindow>,
  limit = 5,
) {
  return useQuery({
    queryKey: ['vehicles', 'recent-plates', id, window, limit],
    queryFn: async () => {
      const response = await api.get<VehicleRecentPlatesResponse>(
        `/vehicles/${id.value}/recent-plates`,
        {
          params: {
            since: window.value.since.toISOString(),
            until: window.value.until.toISOString(),
            limit,
          },
        },
      )
      return response.data
    },
    enabled: computed(() => !!id.value),
    staleTime: 30_000,
  })
}

export function useCreateVehicle() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: VehicleCreate) => {
      const response = await api.post<Vehicle>('/vehicles', body)
      return response.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['vehicles'] })
      // Создание могло сразу привязать девайс (device_id) — обновляем и их список.
      qc.invalidateQueries({ queryKey: ['devices'] })
    },
  })
}

export function useUpdateVehicle() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: VehicleUpdate }) => {
      const response = await api.patch<Vehicle>(`/vehicles/${id}`, body)
      return response.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vehicles'] }),
  })
}

export function useDeleteVehicle() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/vehicles/${id}`)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['vehicles'] })
      // Удаление машины отвязывает её девайс — он снова свободен.
      qc.invalidateQueries({ queryKey: ['devices'] })
    },
  })
}
